import type { DronePoint, DroneScanner } from "@endclock/shared";
import type { Camera } from "../engine/camera.js";
import { mat4Identity, type Mat4 } from "../engine/math.js";
import type { MeshGeometry } from "../engine/mesh.js";
import type { GpuMesh, Renderer } from "../engine/renderer.js";
import type { InterpolatedDroneTransform } from "./interpolation.js";

const vertexSource = `#version 300 es
layout(location=0) in vec3 aPosition;
uniform mat4 uViewProjection;
uniform mat4 uModel;
void main() { gl_Position = uViewProjection * uModel * vec4(aPosition, 1.0); }`;
const fragmentSource = `#version 300 es
precision highp float;
uniform vec4 uColor;
out vec4 outColor;
void main() { outColor = uColor; }`;

/** Two meshes (88 triangles), 136 rendered triangles per scan; meshes and transform storage are allocated once. */
export class DroneScannerEffect {
  private readonly beam: GpuMesh;
  private readonly ring: GpuMesh;
  private readonly program: WebGLProgram;
  private readonly viewProjection: WebGLUniformLocation | null;
  private readonly model: WebGLUniformLocation | null;
  private readonly color: WebGLUniformLocation | null;
  private readonly matrix = mat4Identity();
  private readonly emitter = { x: 0, y: 0, z: 0 };
  private readonly target = { x: 0, y: 0, z: 0 };
  drawCalls = 0;

  constructor(private readonly renderer: Renderer) {
    const gl = renderer.gl;
    this.program = createProgram(gl);
    this.viewProjection = gl.getUniformLocation(this.program, "uViewProjection");
    this.model = gl.getUniformLocation(this.program, "uModel");
    this.color = gl.getUniformLocation(this.program, "uColor");
    this.beam = renderer.createMesh(createScannerBeam());
    this.ring = renderer.createMesh(createScannerRing());
  }

  render(camera: Camera, transform: InterpolatedDroneTransform, scanner: DroneScanner, serverNow: number): void {
    this.drawCalls = 0;
    if (scanner.mode === "idle") return;
    const gl = this.renderer.gl;
    const sine = Math.sin(transform.yaw), cosine = Math.cos(transform.yaw);
    this.emitter.x = transform.x + sine * 0.65;
    this.emitter.y = transform.y + 0.327;
    this.emitter.z = transform.z + cosine * 0.65;
    if (scanner.target) {
      this.target.x = scanner.target.x;
      this.target.y = scanner.target.y;
      this.target.z = scanner.target.z;
    } else if (scanner.mode === "sweep") {
      const sweep = transform.yaw + Math.sin(serverNow * 0.0012) * 0.45;
      this.target.x = this.emitter.x + Math.sin(sweep) * 6;
      this.target.y = this.emitter.y - 3.2;
      this.target.z = this.emitter.z + Math.cos(sweep) * 6;
    } else return;
    const length = Math.hypot(this.target.x - this.emitter.x, this.target.y - this.emitter.y, this.target.z - this.emitter.z);
    if (!Number.isFinite(length) || length < 0.05 || length > 60) return;

    // Preserve every state touched, including separate RGB/alpha blend functions/equations.
    const previousProgram = gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null;
    const previousVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null;
    const blend = gl.isEnabled(gl.BLEND), depth = gl.isEnabled(gl.DEPTH_TEST), cull = gl.isEnabled(gl.CULL_FACE);
    const depthWrite = gl.getParameter(gl.DEPTH_WRITEMASK) as boolean;
    const srcRgb = gl.getParameter(gl.BLEND_SRC_RGB) as number, dstRgb = gl.getParameter(gl.BLEND_DST_RGB) as number;
    const srcAlpha = gl.getParameter(gl.BLEND_SRC_ALPHA) as number, dstAlpha = gl.getParameter(gl.BLEND_DST_ALPHA) as number;
    const equationRgb = gl.getParameter(gl.BLEND_EQUATION_RGB) as number, equationAlpha = gl.getParameter(gl.BLEND_EQUATION_ALPHA) as number;
    try {
      gl.useProgram(this.program);
      gl.uniformMatrix4fv(this.viewProjection, false, camera.viewProjection);
      gl.enable(gl.BLEND);
      gl.enable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);
      gl.depthMask(false);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      // A thin bright ray inside a faint tapered volume; the terrain/player remain readable.
      writeBeamTransform(this.matrix, this.emitter, this.target, 0.32, 1);
      this.draw(this.beam, 0.30, 0.94, 1, 0.10);
      writeBeamTransform(this.matrix, this.emitter, this.target, 0.021, 1);
      this.draw(this.beam, 0.63, 0.97, 1, 0.83);
      // Small source halo, coincident with the GLB's scanning lens.
      writeBeamTransform(this.matrix, this.emitter, this.target, 0.14, 0.022);
      this.draw(this.beam, 0.74, 0.98, 1, 0.85);
      const radius = 0.48 + (Math.sin(serverNow * 0.0035) + 1) * 0.13;
      this.matrix.fill(0);
      this.matrix[0] = radius;
      this.matrix[5] = radius;
      this.matrix[10] = radius;
      this.matrix[12] = this.target.x;
      this.matrix[13] = this.target.y + Math.sin(serverNow * 0.004) * 0.36;
      this.matrix[14] = this.target.z;
      this.matrix[15] = 1;
      this.draw(this.ring, 0.45, 0.96, 1, 0.82);
    } finally {
      gl.depthMask(depthWrite);
      gl.blendFuncSeparate(srcRgb, dstRgb, srcAlpha, dstAlpha);
      gl.blendEquationSeparate(equationRgb, equationAlpha);
      if (blend) gl.enable(gl.BLEND); else gl.disable(gl.BLEND);
      if (depth) gl.enable(gl.DEPTH_TEST); else gl.disable(gl.DEPTH_TEST);
      if (cull) gl.enable(gl.CULL_FACE); else gl.disable(gl.CULL_FACE);
      gl.bindVertexArray(previousVao);
      gl.useProgram(previousProgram);
    }
  }

  dispose(): void {
    this.renderer.destroyMesh(this.beam);
    this.renderer.destroyMesh(this.ring);
    this.renderer.gl.deleteProgram(this.program);
  }

  private draw(mesh: GpuMesh, red: number, green: number, blue: number, alpha: number): void {
    const gl = this.renderer.gl;
    gl.bindVertexArray(mesh.vertexArray);
    gl.uniformMatrix4fv(this.model, false, this.matrix);
    gl.uniform4f(this.color, red, green, blue, alpha);
    gl.drawElements(gl.TRIANGLES, mesh.indexCount, gl.UNSIGNED_SHORT, 0);
    this.renderer.metrics.drawCalls += 1;
    this.renderer.metrics.aiDrawCalls += 1;
    this.renderer.metrics.aiTriangles += mesh.triangleCount;
    this.renderer.metrics.triangles += mesh.triangleCount;
    this.drawCalls += 1;
  }
}

export function writeBeamTransform(matrix: Mat4, start: DronePoint, end: DronePoint, radius: number, lengthScale: number): void {
  let zx = end.x - start.x, zy = end.y - start.y, zz = end.z - start.z;
  const length = Math.hypot(zx, zy, zz) || 1;
  zx /= length; zy /= length; zz /= length;
  let xx = zz, xy = 0, xz = -zx;
  if (Math.abs(zy) > 0.98) { xx = 0; xy = zz; xz = -zy; }
  const rightLength = Math.hypot(xx, xy, xz) || 1;
  xx /= rightLength; xy /= rightLength; xz /= rightLength;
  matrix[0] = xx * radius; matrix[1] = xy * radius; matrix[2] = xz * radius; matrix[3] = 0;
  matrix[4] = (zy * xz - zz * xy) * radius;
  matrix[5] = (zz * xx - zx * xz) * radius;
  matrix[6] = (zx * xy - zy * xx) * radius; matrix[7] = 0;
  matrix[8] = zx * length * lengthScale; matrix[9] = zy * length * lengthScale; matrix[10] = zz * length * lengthScale; matrix[11] = 0;
  matrix[12] = start.x; matrix[13] = start.y; matrix[14] = start.z; matrix[15] = 1;
}

export function createScannerBeam(): MeshGeometry {
  const positions: number[] = [], normals: number[] = [], indices: number[] = [];
  for (let segment = 0; segment < 12; segment += 1) {
    const angle = segment / 12 * Math.PI * 2;
    for (const z of [0, 1]) {
      const radius = z === 0 ? 0.22 : 1;
      positions.push(Math.cos(angle) * radius, Math.sin(angle) * radius, z);
      normals.push(Math.cos(angle), Math.sin(angle), 0);
    }
    const next = (segment + 1) % 12 * 2, current = segment * 2;
    indices.push(current, next, current + 1, next, next + 1, current + 1);
  }
  return { positions: new Float32Array(positions), normals: new Float32Array(normals), indices: new Uint16Array(indices) };
}

export function createScannerRing(): MeshGeometry {
  const positions: number[] = [], normals: number[] = [], indices: number[] = [];
  for (let segment = 0; segment < 32; segment += 1) {
    const angle = segment / 32 * Math.PI * 2;
    for (const radius of [0.955, 1.045]) { positions.push(Math.cos(angle) * radius, 0, Math.sin(angle) * radius); normals.push(0, 1, 0); }
    const next = (segment + 1) % 32 * 2, current = segment * 2;
    indices.push(current, next, current + 1, next, next + 1, current + 1);
  }
  return { positions: new Float32Array(positions), normals: new Float32Array(normals), indices: new Uint16Array(indices) };
}

function createProgram(gl: WebGL2RenderingContext): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error("Drone scanner program allocation failed");
  for (const [type, source] of [[gl.VERTEX_SHADER, vertexSource], [gl.FRAGMENT_SHADER, fragmentSource]] as const) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("Drone scanner shader allocation failed");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) { gl.deleteShader(shader); gl.deleteProgram(program); throw new Error("Drone scanner shader compilation failed"); }
    gl.attachShader(program, shader);
    gl.deleteShader(shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { gl.deleteProgram(program); throw new Error("Drone scanner program linking failed"); }
  return program;
}
